import { __extends } from "tslib";
import { Matrix3D, Rectangle, AssetEvent, PerspectiveProjection } from '@awayjs/core';
import { DirectionalShadowMapper, _Shader_DirectionalShadowMapper } from './DirectionalShadowMapper';
var CascadeShadowMapper = /** @class */ (function (_super) {
    __extends(CascadeShadowMapper, _super);
    function CascadeShadowMapper(image2D, numCascades) {
        if (image2D === void 0) { image2D = null; }
        if (numCascades === void 0) { numCascades = 3; }
        var _this = _super.call(this, image2D) || this;
        _this._scissorRectsInvalid = true;
        _this._onClearDelegate = function (event) { return _this.onClear(event); };
        image2D.addEventListener(AssetEvent.CLEAR, _this._onClearDelegate);
        if (numCascades < 1 || numCascades > 4)
            throw new Error('numCascades must be an integer between 1 and 4');
        _this._numCascades = numCascades;
        _this.init();
        return _this;
    }
    Object.defineProperty(CascadeShadowMapper.prototype, "assetType", {
        /**
         * @inheritDoc
         */
        get: function () {
            return CascadeShadowMapper.assetType;
        },
        enumerable: true,
        configurable: true
    });
    CascadeShadowMapper.prototype.dispose = function () {
        this._image2D.removeEventListener(AssetEvent.CLEAR, this._onClearDelegate);
        _super.prototype.dispose.call(this);
    };
    CascadeShadowMapper.prototype.getSplitRatio = function (index) {
        return this._splitRatios[index];
    };
    CascadeShadowMapper.prototype.setSplitRatio = function (index, value) {
        if (value < 0)
            value = 0;
        else if (value > 1)
            value = 1;
        if (index >= this._numCascades)
            throw new Error('index must be smaller than the number of cascades!');
        this._splitRatios[index] = value;
    };
    CascadeShadowMapper.prototype.getDepthProjections = function (partition) {
        return this._depthProjections[partition].viewMatrix3D;
    };
    CascadeShadowMapper.prototype.init = function () {
        this._splitRatios = new Array(this._numCascades);
        this._nearPlaneDistances = new Array(this._numCascades);
        var s = 1;
        for (var i = this._numCascades - 1; i >= 0; --i) {
            this._splitRatios[i] = s;
            s *= .4;
        }
        this._texOffsetsX = Array(-1, 1, -1, 1);
        this._texOffsetsY = Array(1, 1, -1, -1);
        this._pScissorRects = new Array(4);
        this._depthProjections = new Array();
        for (i = 0; i < this._numCascades; ++i)
            this._depthProjections[i] = new PerspectiveProjection();
    };
    /**
     *
     */
    CascadeShadowMapper.prototype.onClear = function (event) {
        this._scissorRectsInvalid = true;
    };
    Object.defineProperty(CascadeShadowMapper.prototype, "numCascades", {
        get: function () {
            return this._numCascades;
        },
        set: function (value) {
            if (value == this._numCascades)
                return;
            if (value < 1 || value > 4)
                throw new Error('numCascades must be an integer between 1 and 4');
            this._numCascades = value;
            this._scissorRectsInvalid = true;
            this.init();
            this.dispatchEvent(new AssetEvent(AssetEvent.INVALIDATE, this));
        },
        enumerable: true,
        configurable: true
    });
    CascadeShadowMapper.prototype._renderMap = function (partition, renderGroup) {
        if (this._scissorRectsInvalid)
            this.updateScissorRects();
        //renderGroup.depthRenderGroup.getRenderer(partition).cullPlanes = this._cullPlanes;
        //rootRenderer.getDepthRenderer()._iRenderCascades(this._overallDepthProjection, view, this._image2D, this._numCascades, this._pScissorRects, this._depthProjections);
    };
    CascadeShadowMapper.prototype.updateScissorRects = function () {
        var half = this._image2D.width * .5;
        this._pScissorRects[0] = new Rectangle(0, 0, half, half);
        this._pScissorRects[1] = new Rectangle(half, 0, half, half);
        this._pScissorRects[2] = new Rectangle(0, half, half, half);
        this._pScissorRects[3] = new Rectangle(half, half, half, half);
        this._scissorRectsInvalid = false;
    };
    CascadeShadowMapper.prototype._updateProjection = function (projection) {
        var matrix;
        var projectionNear = projection.near;
        var projectionRange = projection.far - projectionNear;
        this._updateProjectionFromFrustumCorners(projection, projection.viewFrustumCorners, this._matrix);
        this._matrix.appendScale(.96, .96, 1);
        this._overallDepthProjection.frustumMatrix3D = this._matrix;
        this._updateCullPlanes(projection);
        for (var i = 0; i < this._numCascades; ++i) {
            matrix = this._depthProjections[i].frustumMatrix3D;
            this._nearPlaneDistances[i] = projectionNear + this._splitRatios[i] * projectionRange;
            this._depthProjections[i].transform.matrix3D = this._overallDepthProjection.transform.matrix3D;
            this.updateProjectionPartition(matrix, this._splitRatios[i], this._texOffsetsX[i], this._texOffsetsY[i]);
            this._depthProjections[i].frustumMatrix3D = matrix;
        }
    };
    CascadeShadowMapper.prototype.updateProjectionPartition = function (matrix, splitRatio, texOffsetX, texOffsetY) {
        var xN, yN, zN;
        var xF, yF, zF;
        var minX = Number.POSITIVE_INFINITY, minY = Number.POSITIVE_INFINITY, minZ;
        var maxX = Number.NEGATIVE_INFINITY, maxY = Number.NEGATIVE_INFINITY, maxZ = Number.NEGATIVE_INFINITY;
        var i = 0;
        while (i < 12) {
            xN = this._localFrustum[i];
            yN = this._localFrustum[i + 1];
            zN = this._localFrustum[i + 2];
            xF = xN + (this._localFrustum[i + 12] - xN) * splitRatio;
            yF = yN + (this._localFrustum[i + 13] - yN) * splitRatio;
            zF = zN + (this._localFrustum[i + 14] - zN) * splitRatio;
            if (xN < minX)
                minX = xN;
            if (xN > maxX)
                maxX = xN;
            if (yN < minY)
                minY = yN;
            if (yN > maxY)
                maxY = yN;
            if (zN > maxZ)
                maxZ = zN;
            if (xF < minX)
                minX = xF;
            if (xF > maxX)
                maxX = xF;
            if (yF < minY)
                minY = yF;
            if (yF > maxY)
                maxY = yF;
            if (zF > maxZ)
                maxZ = zF;
            i += 3;
        }
        minZ = 1;
        var w = (maxX - minX);
        var h = (maxY - minY);
        var d = 1 / (maxZ - minZ);
        if (minX < 0)
            minX -= this._snap; // because int() rounds up for < 0
        if (minY < 0)
            minY -= this._snap;
        minX = Math.floor(minX / this._snap) * this._snap;
        minY = Math.floor(minY / this._snap) * this._snap;
        var snap2 = 2 * this._snap;
        w = Math.floor(w / snap2 + 1) * snap2;
        h = Math.floor(h / snap2 + 1) * snap2;
        maxX = minX + w;
        maxY = minY + h;
        w = 1 / w;
        h = 1 / h;
        var raw = matrix._rawData;
        raw[0] = 2 * w;
        raw[5] = 2 * h;
        raw[10] = d;
        raw[12] = -(maxX + minX) * w;
        raw[13] = -(maxY + minY) * h;
        raw[14] = -minZ * d;
        raw[15] = 1;
        raw[1] = raw[2] = raw[3] = raw[4] = raw[6] = raw[7] = raw[8] = raw[9] = raw[11] = 0;
        matrix.appendScale(.96, .96, 1);
        matrix.appendTranslation(texOffsetX, texOffsetY, 0);
        matrix.appendScale(.5, .5, 1);
    };
    Object.defineProperty(CascadeShadowMapper.prototype, "_iNearPlaneDistances", {
        get: function () {
            return this._nearPlaneDistances;
        },
        enumerable: true,
        configurable: true
    });
    CascadeShadowMapper.assetType = '[asset CascadeShadowMapper]';
    return CascadeShadowMapper;
}(DirectionalShadowMapper));
export { CascadeShadowMapper };
import { ShaderBase } from '@awayjs/renderer';
/**
 * _Shader_CascadeShadowMapper is a shadow map method to apply cascade shadow mapping on materials.
 * Must be used with a DirectionalLight with a CascadeShadowMapper assigned to its shadowMapper property.
 *
 * @see away.lights.CascadeShadowMapper
 */
var _Shader_CascadeShadowMapper = /** @class */ (function (_super) {
    __extends(_Shader_CascadeShadowMapper, _super);
    function _Shader_CascadeShadowMapper() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    /**
     * @inheritDoc
     */
    _Shader_CascadeShadowMapper.prototype._initVO = function (chunkVO) {
        _super.prototype._initVO.call(this, chunkVO);
        chunkVO.needsProjection = true;
    };
    /**
     * @inheritDoc
     */
    _Shader_CascadeShadowMapper.prototype._initConstants = function () {
        _super.prototype._initConstants.call(this);
        var fragmentData = this._shader.fragmentConstantData;
        var vertexData = this._shader.vertexConstantData;
        var index = this._fragmentConstantsIndex;
        fragmentData[index] = 1.0;
        fragmentData[index + 1] = 1 / 255.0;
        fragmentData[index + 2] = 1 / 65025.0;
        fragmentData[index + 3] = 1 / 16581375.0;
        fragmentData[index + 6] = .5;
        fragmentData[index + 7] = -.5;
        index = this._vertexConstantsIndex;
        vertexData[index] = .5;
        vertexData[index + 1] = -.5;
        vertexData[index + 2] = 0;
        var numCascades = this._mapper.numCascades;
        this._projectionMatrices = new Array(numCascades);
        for (var k = 0; k < numCascades; ++k)
            this._projectionMatrices[k] = new Matrix3D(new Float32Array(this._shader.vertexConstantData.buffer, (this._vertexConstantsIndex + 4 + k * 16) * 4, 16));
    };
    /**
     * @inheritDoc
     */
    _Shader_CascadeShadowMapper.prototype._cleanCompilationData = function () {
        _super.prototype._cleanCompilationData.call(this);
        this._cascadeProjections = null;
        this._depthMapCoordVaryings = null;
        this._projectionMatrices = null;
    };
    /**
     * @inheritDoc
     */
    _Shader_CascadeShadowMapper.prototype._getVertexCode = function (registerCache, sharedRegisters) {
        var code = '';
        var dataReg = registerCache.getFreeVertexConstant();
        this._vertexConstantsIndex = dataReg.index * 4;
        //Create the registers for the cascades' projection coordinates.
        this._cascadeProjections = new Array(this._mapper.numCascades);
        this._depthMapCoordVaryings = new Array(this._mapper.numCascades);
        for (var i = 0; i < this._mapper.numCascades; ++i) {
            this._depthMapCoordVaryings[i] = registerCache.getFreeVarying();
            this._cascadeProjections[i] = registerCache.getFreeVertexConstant();
            registerCache.getFreeVertexConstant();
            registerCache.getFreeVertexConstant();
            registerCache.getFreeVertexConstant();
        }
        var temp = registerCache.getFreeVertexVectorTemp();
        for (var i = 0; i < this._mapper.numCascades; ++i) {
            code += 'm44 ' + temp + ', ' + sharedRegisters.globalPositionVertex + ', ' + this._cascadeProjections[i] + '\n' +
                'add ' + this._depthMapCoordVaryings[i] + ', ' + temp + ', ' + dataReg + '.zzwz\n';
        }
        return code;
    };
    /**
     * @inheritDoc
     */
    _Shader_CascadeShadowMapper.prototype._getFragmentCode = function (targetReg, registerCache, sharedRegisters) {
        var numCascades = this._mapper.numCascades;
        var decReg = registerCache.getFreeFragmentConstant();
        var dataReg = registerCache.getFreeFragmentConstant();
        var planeDistanceReg = registerCache.getFreeFragmentConstant();
        var planeDistances = Array(planeDistanceReg + '.x', planeDistanceReg + '.y', planeDistanceReg + '.z', planeDistanceReg + '.w');
        var code;
        this._fragmentConstantsIndex = decReg.index * 4;
        var inQuad = registerCache.getFreeFragmentVectorTemp();
        registerCache.addFragmentTempUsages(inQuad, 1);
        var uvCoord = registerCache.getFreeFragmentVectorTemp();
        registerCache.addFragmentTempUsages(uvCoord, 1);
        // assume lowest partition is selected, will be overwritten later otherwise
        code = 'mov ' + uvCoord + ', ' + this._depthMapCoordVaryings[numCascades - 1] + '\n';
        for (var i = numCascades - 2; i >= 0; --i) {
            var uvProjection = this._depthMapCoordVaryings[i];
            // calculate if in texturemap (result == 0 or 1, only 1 for a single partition)
            code += 'slt ' + inQuad + '.z, ' + sharedRegisters.projectionFragment + '.z, ' + planeDistances[i] + '\n'; // z = x > minX, w = y > minY
            var temp = registerCache.getFreeFragmentVectorTemp();
            // linearly interpolate between old and new uv coords using predicate value == conditional toggle to new value if predicate == 1 (true)
            code += 'sub ' + temp + ', ' + uvProjection + ', ' + uvCoord + '\n' +
                'mul ' + temp + ', ' + temp + ', ' + inQuad + '.z\n' +
                'add ' + uvCoord + ', ' + uvCoord + ', ' + temp + '\n';
        }
        registerCache.removeFragmentTempUsage(inQuad);
        code += 'div ' + uvCoord + ', ' + uvCoord + ', ' + uvCoord + '.w\n' +
            'mul ' + uvCoord + '.xy, ' + uvCoord + '.xy, ' + dataReg + '.zw\n' +
            'add ' + uvCoord + '.xy, ' + uvCoord + '.xy, ' + dataReg + '.zz\n';
        // code += (<ShadowChunkBase> this._baseChunk)._getCascadeFragmentCode(decReg, uvCoord, targetReg, registerCache, sharedRegisters) +
        // 	"add " + targetReg + ".w, " + targetReg + ".w, " + dataReg + ".y\n";
        //
        registerCache.removeFragmentTempUsage(uvCoord);
        return code;
    };
    /**
     * @inheritDoc
     */
    _Shader_CascadeShadowMapper.prototype._activate = function () {
        // (<ShadowChunkBase> this._baseChunk).depthMap.activate();
        this._shader.vertexConstantData[this._vertexConstantsIndex + 3] = -1 / (this._mapper.depth * this._mapper.epsilon);
        var numCascades = this._mapper.numCascades;
        for (var k = 0; k < numCascades; ++k)
            this._projectionMatrices[k].copyFrom(this._mapper.getDepthProjections(k), true);
        var fragmentData = this._shader.fragmentConstantData;
        var fragmentIndex = this._fragmentConstantsIndex;
        fragmentData[fragmentIndex + 5] = 1 - this._mapper.alpha;
        var nearPlaneDistances = this._mapper._iNearPlaneDistances;
        fragmentIndex += 8;
        for (var i = 0; i < numCascades; ++i)
            fragmentData[fragmentIndex + i] = nearPlaneDistances[i];
        // (<ShadowChunkBase> this._baseChunk)._activateForCascade();
    };
    /**
     * @inheritDoc
     */
    _Shader_CascadeShadowMapper.prototype._setRenderState = function (renderState) {
    };
    return _Shader_CascadeShadowMapper;
}(_Shader_DirectionalShadowMapper));
export { _Shader_CascadeShadowMapper };
ShaderBase.registerAbstraction(_Shader_CascadeShadowMapper, CascadeShadowMapper);
